import { supabase } from './supabaseService';
import { Organization, Role, User } from '../types/multiTenant';

/**
 * Organization Service
 * Handles all organization-related operations with Supabase
 */
class OrganizationService {
  /**
   * Get organization by ID
   */
  async getOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching organization:', error);
        return null;
      }

      return this.transformOrganization(data);
    } catch (error) {
      console.error('Error in getOrganization:', error);
      return null;
    }
  }

  /**
   * Get organization by slug
   */
  async getOrganizationBySlug(slug: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('slug', slug)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching organization by slug:', error);
        return null;
      }

      return this.transformOrganization(data);
    } catch (error) {
      console.error('Error in getOrganizationBySlug:', error);
      return null;
    }
  }

  /**
   * Get all roles for an organization
   */
  async getOrganizationRoles(organizationId: string): Promise<Role[]> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .order('name');

      if (error) {
        console.error('Error fetching roles:', error);
        return [];
      }

      return (data || []).map(role => this.transformRole(role));
    } catch (error) {
      console.error('Error in getOrganizationRoles:', error);
      return [];
    }
  }

  /**
   * Get all users for an organization
   */
  async getOrganizationUsers(organizationId: string): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          role:roles(*)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .order('full_name');

      if (error) {
        console.error('Error fetching users:', error);
        return [];
      }

      return (data || []).map(user => this.transformUser(user));
    } catch (error) {
      console.error('Error in getOrganizationUsers:', error);
      return [];
    }
  }

  /**
   * Get user by ID with role information
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          role:roles(*)
        `)
        .eq('id', userId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching user:', error);
        return null;
      }

      return this.transformUser(data);
    } catch (error) {
      console.error('Error in getUserById:', error);
      return null;
    }
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          role:roles(*)
        `)
        .eq('email', email)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching user by email:', error);
        return null;
      }

      return this.transformUser(data);
    } catch (error) {
      console.error('Error in getUserByEmail:', error);
      return null;
    }
  }

  /**
   * Create new organization
   * @param data - Organization data including optional invite key and selected plan
   */
  async createOrganization(data: {
    name: string;
    businessType: Organization['businessType'];
    adminEmail: string;
    adminName: string;
    adminPhone?: string;
    selectedPlanId?: string; // The plan selected by user during signup
    inviteKey?: { planId: string; trialDays: number; keyId: string };
  }): Promise<{ organization: Organization; user: User } | null> {
    try {
      // Generate slug
      const slug = this.generateSlug(data.name);

      // Determine plan and trial period
      // Priority: invite key plan > selected plan > default 'trial'
      const planId = data.inviteKey?.planId || data.selectedPlanId || 'trial';
      const trialDays = data.inviteKey?.trialDays || (planId === 'trial' ? 30 : 0);
      const hasTrialPeriod = trialDays > 0 || planId === 'trial';

      // DEBUG: Log values to understand subscription status logic
      console.log('=== ORG SERVICE CREATE DEBUG ===');
      console.log('data.selectedPlanId:', data.selectedPlanId);
      console.log('data.inviteKey:', data.inviteKey);
      console.log('Final planId:', planId);
      console.log('trialDays:', trialDays);
      console.log('hasTrialPeriod:', hasTrialPeriod);
      console.log('subscription_status will be:', hasTrialPeriod ? 'trialing' : 'incomplete');
      console.log('================================');

      // Calculate limits based on plan
      const planLimits = this.getPlanLimits(planId);

      // Create organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: data.name,
          slug,
          business_type: data.businessType,
          plan_id: planId,
          subscription_status: hasTrialPeriod ? 'trialing' : 'incomplete',
          trial_ends_at: hasTrialPeriod
            ? new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toISOString()
            : null,
          max_users: planLimits.maxUsers,
          max_clients: planLimits.maxClients,
        })
        .select()
        .single();

      if (orgError) {
        console.error('Error creating organization:', {
          code: orgError.code,
          message: orgError.message,
          details: orgError.details,
          hint: orgError.hint,
          full: orgError,
        });
        return null;
      }

      const organization = this.transformOrganization(orgData);

      // Get admin role (created by trigger)
      const { data: roleData, error: roleError } = await supabase
        .from('roles')
        .select('*')
        .eq('organization_id', organization.id)
        .eq('name', 'admin')
        .single();

      if (roleError || !roleData) {
        console.error('Error fetching admin role:', roleError);
        return null;
      }

      // Create admin user
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert({
          email: data.adminEmail,
          organization_id: organization.id,
          role_id: roleData.id,
          full_name: data.adminName,
          phone: data.adminPhone || null,
          is_active: true,
          invitation_accepted_at: new Date().toISOString(),
        })
        .select(`
          *,
          role:roles(*)
        `)
        .single();

      if (userError) {
        console.error('Error creating admin user:', userError);
        return null;
      }

      const user = this.transformUser(userData);

      return { organization, user };
    } catch (error) {
      console.error('Error in createOrganization:', error);
      return null;
    }
  }

  /**
   * Update organization
   */
  async updateOrganization(
    organizationId: string,
    updates: Partial<Organization>
  ): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .update({
          name: updates.name,
          logo_url: updates.logoUrl,
          config: updates.config,
        })
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
        return null;
      }

      return this.transformOrganization(data);
    } catch (error) {
      console.error('Error in updateOrganization:', error);
      return null;
    }
  }

  /**
   * Check if user has permission
   */
  hasPermission(
    role: Role | null,
    entity: string,
    action: 'create' | 'read' | 'update' | 'delete'
  ): boolean {
    if (!role || !role.permissions) {
      return false;
    }

    const entityPermissions = (role.permissions as any)[entity];
    if (!entityPermissions) {
      return false;
    }

    return (entityPermissions as any)[action] === true;
  }

  /**
   * Transform database organization to app format
   */
  private transformOrganization(data: any): Organization {
    return {
      id: data.id,
      name: data.name,
      slug: data.slug,
      businessType: data.business_type,
      logoUrl: data.logo_url,
      planId: data.plan_id,
      subscriptionStatus: data.subscription_status,
      trialEndsAt: data.trial_ends_at,
      subscriptionEndsAt: data.subscription_ends_at,
      config: data.config || {},
      maxUsers: data.max_users,
      maxClients: data.max_clients,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }

  /**
   * Transform database role to app format
   */
  private transformRole(data: any): Role {
    return {
      id: data.id,
      organizationId: data.organization_id,
      name: data.name,
      displayName: data.display_name,
      permissions: data.permissions || {},
      isSystemRole: data.is_system_role,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }

  /**
   * Transform database user to app format
   */
  private transformUser(data: any): User {
    return {
      id: data.id,
      email: data.email,
      organizationId: data.organization_id,
      roleId: data.role_id,
      fullName: data.full_name,
      avatarUrl: data.avatar_url,
      phone: data.phone,
      isActive: data.is_active,
      invitedBy: data.invited_by,
      invitationAcceptedAt: data.invitation_accepted_at,
      preferences: data.preferences || {},
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
      lastLoginAt: data.last_login_at,
      role: data.role ? this.transformRole(data.role) : undefined,
    };
  }

  /**
   * Get plan limits based on plan ID
   */
  private getPlanLimits(planId: string): { maxUsers: number; maxClients: number } {
    const planLimits: Record<string, { maxUsers: number; maxClients: number }> = {
      trial: { maxUsers: 1, maxClients: 30 },
      individual: { maxUsers: 1, maxClients: -1 },
      small_clinic: { maxUsers: 5, maxClients: -1 },
      clinic_pro: { maxUsers: 15, maxClients: -1 },
      enterprise: { maxUsers: -1, maxClients: -1 },
    };

    return planLimits[planId] || planLimits.individual;
  }

  /**
   * Generate URL-friendly slug from name with unique suffix
   */
  private generateSlug(name: string): string {
    const baseSlug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

    // Add unique suffix (timestamp + random) to avoid conflicts
    const uniqueSuffix = `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 6)}`;
    return `${baseSlug}-${uniqueSuffix}`;
  }

  /**
   * Check if slug already exists
   */
  private async slugExists(slug: string): Promise<boolean> {
    const { data } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    return !!data;
  }
}

export const organizationService = new OrganizationService();
export default organizationService;
